import { createStore } from 'vuex';
import CryptoJS from 'crypto-js';
import { authApi, tokenManager, refreshAccessSession } from '@/services/api';
import {
  getTokenRoleInfo,
  isAccessTokenExpired,
  isAccessTokenFresh,
} from '@/utils/authToken';

/** 대리 로그인·기관 화면 진입 시에도 실제 조작자 권한 레벨 */
const resolveOperatorRoleLevel = (state: AuthState): number => {
  if (state.originalUser?.roleLevel != null) {
    return Number(state.originalUser.roleLevel) || 0;
  }
  const fromToken = getTokenRoleInfo();
  if (fromToken.userType === 'user') {
    return fromToken.roleLevel;
  }
  return Number(state.user?.roleLevel) || 0;
};

// 암호화 키
const ENCRYPTION_KEY = 'caionSecretKey2026ByAdmin';

// 도메인별 저장소 키 접두사 (test.caion.kr와 caion.kr 분리)
const getDomainPrefix = (): string => {
  const host = window.location.hostname;
  if (host.includes('test.caion')) return 'test_';
  return '';
};

// 로컬스토리지 키 (도메인별 분리)
const getStorageKeys = () => {
  const prefix = getDomainPrefix();
  return {
    AUTH_STATE: `${prefix}caion_auth`,
    SAVED_ID: `${prefix}caion_saved_id`,
    AUTO_LOGIN: `${prefix}caion_auto_login`,
    AUTO_LOGIN_PREF: `${prefix}caion_auto_login_pref`,
    ORIGINAL_USER: `${prefix}caion_original_user`
  };
};

// 초기 로드용 (store 생성 시점)
const STORAGE_KEYS = getStorageKeys();

// 사업구분 멤버 정보 인터페이스
interface BizGubunMember {
  idx: number;
  name: string;
  code: string;
  bizGubun: string;
  bizGubunName: string;
  bizGubunColor: string;
  orgName: string;
}

// 통합 사용자 인터페이스 (멤버/유저 공통)
interface AuthUser {
  idx: number;
  id: string;
  name: string;
  email: string;
  phone: string;
  orgCode: string;
  orgName: string;
  orgLevel: string;
  roleCode: string;
  roleName: string;
  roleLevel: number;
  userType: 'member' | 'user';
  // 멤버 전용 필드
  memberCode?: string;
  bizGubun?: string;
  bizGubunName?: string;
  bizGubunColor?: string;
  bossName?: string;
  hasMultipleBizGubun?: boolean;
  // 유저 전용 필드
  lastLogin?: string;
}

interface SnackbarState {
  show: boolean;
  text: string;
  color: string;
  timeout: number;
}

interface AuthState {
  user: AuthUser | null;
  originalUser: AuthUser | null;  // 대리 로그인 시 원래 최고관리자 정보
  isAutoLogin: boolean;
  savedId: string;
  snackbar: SnackbarState;
  loading: boolean;
}

// 암호화/복호화
const encrypt = (data: any): string => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
};

const decrypt = (encryptedData: string): any => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch {
    return null;
  }
};

const persistSession = (
  user: AuthUser,
  token: string | null | undefined,
  refreshToken: string | null | undefined,
  autoLogin: boolean
) => {
  const keys = getStorageKeys();
  if (token) {
    tokenManager.setToken(token, autoLogin);
  }
  if (autoLogin && refreshToken) {
    tokenManager.setRefreshToken(refreshToken);
  } else if (!autoLogin) {
    tokenManager.removeRefreshToken();
  }
  const blob = encrypt({ user, token, timestamp: Date.now() });
  if (autoLogin) {
    localStorage.setItem(keys.AUTO_LOGIN, blob);
    sessionStorage.removeItem(keys.AUTH_STATE);
  } else {
    localStorage.removeItem(keys.AUTO_LOGIN);
    sessionStorage.setItem(keys.AUTH_STATE, blob);
  }
};

// 저장된 상태 불러오기 (초기 로드용 - 토큰 유효성은 restoreSession에서 체크)
const loadSavedState = (): Partial<AuthState> => {
  const savedState: Partial<AuthState> = {};
  
  // 저장된 ID 불러오기
  const savedId = localStorage.getItem(STORAGE_KEYS.SAVED_ID);
  if (savedId) {
    savedState.savedId = savedId;
  }
  
  // 자동 로그인 체크 - 토큰과 유저 정보 모두 있어야 함
  const autoLoginData = localStorage.getItem(STORAGE_KEYS.AUTO_LOGIN);
  if (autoLoginData) {
    const decrypted = decrypt(autoLoginData);
    // 토큰과 유저 정보 모두 있어야 복원
    if (decrypted && decrypted.user && decrypted.token) {
      savedState.user = decrypted.user;
      savedState.isAutoLogin = true;
    } else {
      // 토큰이 없으면 자동 로그인 데이터 삭제
      console.warn('초기 로드: 토큰 없음 - 자동 로그인 데이터 삭제');
      localStorage.removeItem(STORAGE_KEYS.AUTO_LOGIN);
    }
  }
  
  return savedState;
};

// 초기 상태
const initialState = loadSavedState();

const store = createStore<AuthState>({
  state: {
    user: initialState.user || null,
    originalUser: null,  // 대리 로그인 시 원래 최고관리자 정보
    isAutoLogin: initialState.isAutoLogin || false,
    savedId: initialState.savedId || '',
    snackbar: {
      show: false,
      text: '',
      color: 'info',
      timeout: 4000
    },
    loading: false
  },
  
  mutations: {
    setUser(state, user: AuthUser | null) {
      state.user = user;
    },
    
    setOriginalUser(state, user: AuthUser | null) {
      const keys = getStorageKeys();
      state.originalUser = user;
      if (user) {
        sessionStorage.setItem(keys.ORIGINAL_USER, encrypt({ user }));
      } else {
        sessionStorage.removeItem(keys.ORIGINAL_USER);
      }
    },
    
    setAutoLogin(state, value: boolean) {
      state.isAutoLogin = value;
    },
    
    setSavedId(state, id: string) {
      const keys = getStorageKeys();
      state.savedId = id;
      if (id) {
        localStorage.setItem(keys.SAVED_ID, id);
      } else {
        localStorage.removeItem(keys.SAVED_ID);
      }
    },
    
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    
    showSnackbar(state, { text, color = 'info', timeout = 4000 }) {
      state.snackbar = { show: true, text, color, timeout };
    },
    
    hideSnackbar(state) {
      state.snackbar.show = false;
    },
    
    clearAuth(state) {
      const keys = getStorageKeys();
      state.user = null;
      state.originalUser = null;
      state.isAutoLogin = false;
      localStorage.removeItem(keys.AUTO_LOGIN);
      sessionStorage.removeItem(keys.AUTH_STATE);
      sessionStorage.removeItem(keys.ORIGINAL_USER);
      tokenManager.removeToken();
      tokenManager.removeRefreshToken();
    }
  },
  
  actions: {
    /**
     * 로그인
     */
    async login(
      { commit },
      { loginId, loginPw, saveId = false, autoLogin = false, memberCode }: {
        loginId: string;
        loginPw: string;
        saveId?: boolean;
        autoLogin?: boolean;
        memberCode?: number;
      }
    ) {
      commit('setLoading', true);
      
      try {
        const response = await authApi.login(loginId, loginPw, 'auto', memberCode, autoLogin);

        if (response.data.success && response.data.requireBizGubunSelect) {
          return {
            success: true,
            requireBizGubunSelect: true,
            members: response.data.data?.members || []
          };
        }
        
        if (response.data.success) {
          const user = response.data.data as AuthUser;
          const token = response.data.token;
          const refreshToken = response.data.refreshToken;
          
          commit('setUser', user);
          commit('setSavedId', (saveId || autoLogin) ? loginId : '');
          commit('setAutoLogin', !!autoLogin);
          persistSession(user, token, refreshToken, !!autoLogin);
          
          commit('showSnackbar', { 
            text: `${user.name}님, 환영합니다!`, 
            color: 'success' 
          });
          
          return { success: true, user };
        } else {
          commit('showSnackbar', { 
            text: response.data.message || '로그인에 실패했습니다.', 
            color: 'error' 
          });
          return { success: false, message: response.data.message };
        }
      } catch (error: any) {
        const message = error.response?.data?.message || '로그인 중 오류가 발생했습니다.';
        commit('showSnackbar', { text: message, color: 'error' });
        return { success: false, message };
      } finally {
        commit('setLoading', false);
      }
    },
    
    /**
     * 로그아웃
     */
    async logout({ commit }) {
      const refreshToken = tokenManager.getRefreshToken();
      commit('clearAuth');
      commit('showSnackbar', { text: '로그아웃되었습니다.', color: 'info' });
      if (refreshToken) {
        try {
          await fetch('/api/auth/logout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
            keepalive: true
          });
        } catch {
          /* 서버 폐기 실패해도 로컬은 정리 */
        }
      }
    },

    /**
     * 내 정보(이름/이메일/연락처) 갱신 — 세션 스토리지도 동기화
     */
    updateCurrentUserProfile({ commit, state }, payload: { name?: string; email?: string; phone?: string }) {
      if (!state.user || state.user.userType !== 'user') return false;

      const updatedUser: AuthUser = {
        ...state.user,
        name: payload.name !== undefined ? payload.name : state.user.name,
        email: payload.email !== undefined ? payload.email : state.user.email,
        phone: payload.phone !== undefined ? payload.phone : state.user.phone,
      };

      commit('setUser', updatedUser);

      persistSession(
        updatedUser,
        tokenManager.getToken(),
        tokenManager.getRefreshToken(),
        state.isAutoLogin
      );

      return true;
    },

    
    /**
     * 세션 복원 (페이지 새로고침 시)
     * - 토큰과 유저 정보가 모두 있어야 복원
     * - 토큰이 없으면 인증 정보 클리어
     */
    async restoreSession({ commit, state }) {
      const keys = getStorageKeys();
      
      // 대리 로그인 원래 유저 복원 (먼저 체크)
      const originalData = sessionStorage.getItem(keys.ORIGINAL_USER);
      const isImpersonating = !!originalData;

      if (isImpersonating) {
        // 대리 로그인 중이면 세션 스토리지의 유저(대리 대상)를 우선 사용
        const sessionData = sessionStorage.getItem(keys.AUTH_STATE);
        if (sessionData) {
          const decrypted = decrypt(sessionData);
          // 토큰과 유저 정보 모두 있어야 복원
          if (decrypted && decrypted.user && decrypted.token) {
            commit('setUser', decrypted.user);
            tokenManager.setToken(decrypted.token, false);
          } else {
            // 토큰이 없으면 인증 정보 클리어
            console.warn('세션 복원 실패: 토큰 없음 (대리 로그인)');
            commit('clearAuth');
            return false;
          }
        } else {
          // 세션 데이터 없으면 클리어
          commit('clearAuth');
          return false;
        }
        
        // 원래 유저 복원
        const decryptedOriginal = decrypt(originalData);
        if (decryptedOriginal && decryptedOriginal.user) {
          commit('setOriginalUser', decryptedOriginal.user);
        }
        
        // 자동 로그인 상태 유지
        const autoLoginData = localStorage.getItem(keys.AUTO_LOGIN);
        if (autoLoginData) {
          commit('setAutoLogin', true);
        }
      } else {
        // 대리 로그인이 아닌 일반 세션 복원 — 저장본을 먼저 올린 뒤, 필요할 때만 refresh
        const autoLoginData = localStorage.getItem(keys.AUTO_LOGIN);
        if (autoLoginData) {
          const decrypted = decrypt(autoLoginData);
          if (decrypted && decrypted.user && decrypted.token) {
            commit('setUser', decrypted.user);
            commit('setAutoLogin', true);
            tokenManager.setToken(decrypted.token, true);
          } else {
            console.warn('세션 복원 실패: 토큰 없음 (자동 로그인)');
            localStorage.removeItem(keys.AUTO_LOGIN);
          }
        } else {
          const sessionData = sessionStorage.getItem(keys.AUTH_STATE);
          if (sessionData) {
            const decrypted = decrypt(sessionData);
            if (decrypted && decrypted.user && decrypted.token) {
              commit('setUser', decrypted.user);
              tokenManager.setToken(decrypted.token, false);
            } else {
              console.warn('세션 복원 실패: 토큰 없음 (세션)');
              sessionStorage.removeItem(keys.AUTH_STATE);
            }
          }
        }

        // 자동 로그인: access가 만료·임박할 때만 refresh (매 부팅 강제 회전 금지)
        const access = tokenManager.getToken();
        const hasRefresh = !!tokenManager.getRefreshToken();
        if (hasRefresh && (!access || !isAccessTokenFresh(access))) {
          try {
            const session = await refreshAccessSession();
            if (session?.token) {
              const user = (session.user as AuthUser) || state.user;
              if (session.user) commit('setUser', session.user as AuthUser);
              commit('setAutoLogin', true);
              if (user) {
                persistSession(user, session.token, session.refreshToken, true);
              } else {
                tokenManager.setToken(session.token, true);
                if (session.refreshToken) {
                  tokenManager.setRefreshToken(session.refreshToken);
                }
              }
            }
          } catch (error: any) {
            const status = error?.response?.status;
            const stillOk = tokenManager.getToken();
            if (
              (status === 401 || status === 403) &&
              (!stillOk || isAccessTokenExpired(stillOk))
            ) {
              commit('clearAuth');
              return false;
            }
            // 네트워크 오류·타 탭 회전 등은 저장된 JWT로 유지
          }
        }
      }
      
      return true;
    },
    
    /**
     * 스낵바 표시
     */
    showMessage({ commit }, { text, color = 'info', timeout = 4000 }) {
      commit('showSnackbar', { text, color, timeout });
    },
    
    showSuccess({ commit }, text: string) {
      commit('showSnackbar', { text, color: 'success', timeout: 3000 });
    },
    
    showError({ commit }, text: string) {
      commit('showSnackbar', { text, color: 'error', timeout: 5000 });
    },
    
    /**
     * 대리 로그인 (권한 접속)
     * - 매니저 이상(60+): 유저와 멤버 권한으로 접속 가능
     * - 직원(40): 멤버 권한으로만 접속 가능
     * @param targetUser 대리 로그인할 대상 유저/멤버 정보
     */
    impersonate({ commit, state }, targetUser: AuthUser) {
      const actualLevel = state.originalUser?.roleLevel || state.user?.roleLevel || 0;
      
      // 유저(40+) 이상만 사용 가능
      if (actualLevel < 40) {
        commit('showSnackbar', { text: '권한 접속 기능을 사용할 수 없습니다.', color: 'error' });
        return false;
      }
      
      // 직원(40)은 멤버 권한으로만 접속 가능
      if (actualLevel < 60 && targetUser.userType !== 'member') {
        commit('showSnackbar', { text: '멤버 권한으로만 접속할 수 있습니다.', color: 'error' });
        return false;
      }
      
      // 이미 대리 로그인 중이면 originalUser 유지
      if (!state.originalUser) {
        commit('setOriginalUser', state.user);
      }
      
      // 대상 유저로 전환
      commit('setUser', targetUser);
      
      // 세션 스토리지 업데이트 (토큰 포함 — 새로고침 시 대리 로그인 복원)
      const keys = getStorageKeys();
      const token = tokenManager.getToken();
      sessionStorage.setItem(
        keys.AUTH_STATE,
        encrypt({ user: targetUser, token, timestamp: Date.now() })
      );
      
      commit('showSnackbar', { 
        text: `${targetUser.name}(${targetUser.roleName}) 권한으로 접속합니다.`, 
        color: 'info',
        timeout: 3000
      });
      
      return true;
    },
    
    /**
     * 대리 로그인 종료 - 원래 최고관리자로 복귀
     */
    exitImpersonation({ commit, state }) {
      if (!state.originalUser) {
        commit('showSnackbar', { text: '대리 로그인 상태가 아닙니다.', color: 'warning' });
        return false;
      }
      
      const originalUser = state.originalUser;
      
      // 원래 유저로 복귀
      commit('setUser', originalUser);
      commit('setOriginalUser', null);
      
      sessionStorage.setItem(
        STORAGE_KEYS.AUTH_STATE,
        encrypt({ user: originalUser, token: tokenManager.getToken(), timestamp: Date.now() })
      );
      
      commit('showSnackbar', { 
        text: `${originalUser.name} 권한으로 복귀했습니다.`, 
        color: 'success',
        timeout: 3000
      });
      
      return true;
    },
    
    /**
     * 사업구분 전환 (같은 ID를 가진 다른 멤버로 전환)
     */
    async switchBizGubun({ commit, state }, targetMemberIdx: number) {
      if (!state.user || state.user.userType !== 'member') {
        commit('showSnackbar', { text: '멤버 계정에서만 사용 가능합니다.', color: 'error' });
        return { success: false };
      }
      
      commit('setLoading', true);
      
      try {
        const response = await authApi.switchBizGubun(
          state.user.idx,
          targetMemberIdx,
          tokenManager.getRefreshToken(),
          state.isAutoLogin
        );
        
        if (response.data.success) {
          const newUser = response.data.data as AuthUser;
          const token = response.data.token;
          const refreshToken = response.data.refreshToken;
          
          commit('setUser', newUser);
          persistSession(newUser, token, refreshToken, state.isAutoLogin);
          
          commit('showSnackbar', { 
            text: `${newUser.bizGubunName || newUser.bizGubun}(으)로 전환되었습니다.`, 
            color: 'success' 
          });
          
          return { success: true, user: newUser };
        } else {
          commit('showSnackbar', { 
            text: response.data.message || '사업구분 전환에 실패했습니다.', 
            color: 'error' 
          });
          return { success: false, message: response.data.message };
        }
      } catch (error: any) {
        const message = error.response?.data?.message || '사업구분 전환 중 오류가 발생했습니다.';
        commit('showSnackbar', { text: message, color: 'error' });
        return { success: false, message };
      } finally {
        commit('setLoading', false);
      }
    },
    
    /**
     * 사업구분 목록 조회 (같은 ID를 가진 멤버 목록)
     */
    async fetchBizGubunList({ state }) {
      if (!state.user || state.user.userType !== 'member') {
        return { success: false, data: [] };
      }
      
      try {
        const response = await authApi.getMemberBizGubunList(state.user.id);
        
        if (response.data.success) {
          return { success: true, data: response.data.data };
        }
        return { success: false, data: [] };
      } catch (error) {
        return { success: false, data: [] };
      }
    }
  },
  
  getters: {
    // 로그인 여부
    isLoggedIn: (state) => !!state.user,
    
    // 현재 사용자 정보
    currentUser: (state) => state.user,
    
    // 사용자 타입 (member/user)
    userType: (state) => state.user?.userType || null,
    
    // 조직 코드
    orgCode: (state) => state.user?.orgCode || '',
    
    // 조직명
    orgName: (state) => state.user?.orgName || '',
    
    // 권한 코드
    roleCode: (state) => state.user?.roleCode || null,
    
    // 권한 레벨
    roleLevel: (state) => state.user?.roleLevel || 0,
    
    // 매니저급 여부 (roleLevel 60 이상 — 100/90 다음 단계; ADMIN+는 90+)
    isAdmin: (state) => (state.user?.roleLevel || 0) >= 60,
    
    // 최고관리자 여부 (roleLevel 90 이상)
    isSuperAdmin: (state) => (state.user?.roleLevel || 0) >= 90,
    
    // 실제 권한 레벨 (대리 로그인·기관 화면에서도 JWT 기준 조작자 권한)
    actualRoleLevel: (state) => resolveOperatorRoleLevel(state),
    
    // 실제 최고관리자 여부 (대리 로그인 시에도 원래가 최고관리자)
    isActuallySuperAdmin: (state) => {
      if (state.originalUser) {
        return (state.originalUser.roleLevel || 0) >= 90;
      }
      return (state.user?.roleLevel || 0) >= 90;
    },
    
    // 멤버 권한으로 접속 가능 여부 (유저 40+ 이상)
    canImpersonateMember: (state) => {
      const level = state.originalUser?.roleLevel || state.user?.roleLevel || 0;
      return level >= 40;
    },
    
    // 유저 권한으로 접속 가능 여부 (어드민 60+ 이상)
    canImpersonateUser: (state) => {
      const level = state.originalUser?.roleLevel || state.user?.roleLevel || 0;
      return level >= 60;
    },
    
    // 대리 로그인 중 여부
    isImpersonating: (state) => !!state.originalUser,
    
    // 원래 유저 정보 (대리 로그인 시)
    originalUser: (state) => state.originalUser,
    
    // 회계처리용 임금대장 열람 (매니저 50+ · 기관회원 직접 로그인 제외)
    canViewAccountingWageReport: (_state, getters) => getters.actualRoleLevel >= 50,

    // 멤버 여부
    isMember: (state) => state.user?.userType === 'member',
    
    // 사업구분
    bizGubun: (state) => state.user?.bizGubun || '',
    
    // 사업구분명
    bizGubunName: (state) => state.user?.bizGubunName || state.user?.bizGubun || '',
    
    // 사업구분 색상
    bizGubunColor: (state) => state.user?.bizGubunColor || '#9E9E9E',
    
    // 다중 사업구분 여부
    hasMultipleBizGubun: (state) => state.user?.hasMultipleBizGubun || false,
    
    // 저장된 ID
    savedId: (state) => state.savedId,
    
    // 자동 로그인 여부
    isAutoLogin: (state) => state.isAutoLogin,
    
    // 스낵바 상태
    snackbar: (state) => state.snackbar,
    
    // 로딩 상태
    isLoading: (state) => state.loading
  }
});

export default store;
